import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import { createTicket, TICKET_PRIORITY } from "./ticketApi";
import { PRIORITY_OPTIONS, getProblemTypeOptionsForRole } from "./helpSupportShared";
import "../documents/Documents.css";
import "./HelpSupport.css";

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  return Number(r);
};

const HelpSupportCreatePage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const problemTypeOptions = useMemo(() => getProblemTypeOptionsForRole(getRoleId()), []);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: "",
    description: "",
    problemType: "",
    priority: String(TICKET_PRIORITY.Medium),
  });

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) {
      next.title = t("app.modals.helpSupport.errorTitleRequired");
    }
    if (!form.description.trim()) {
      next.description = t("app.modals.helpSupport.errorDescriptionRequired");
    }
    if (!form.problemType) {
      next.problemType = t("app.modals.helpSupport.errorProblemTypeRequired");
    } else if (!problemTypeOptions.some((opt) => opt.value === form.problemType)) {
      next.problemType = t("app.modals.helpSupport.errorProblemTypeRequired");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const created = await createTicket({
        title: form.title.trim(),
        description: form.description.trim(),
        problemType: form.problemType,
        priority: Number(form.priority),
      });
      Common.showSuccessMessage(
        t("app.helpSupport.createSuccess", {
          defaultValue: "Ticket created. Company admins have been notified.",
        })
      );
      const id = created?.id ?? created?.Id;
      if (id) history.replace(`/help-support/${encodeURIComponent(id)}`);
      else history.replace("/help-support");
    } catch (err) {
      Common.showErrorMessage(
        err?.message || t("app.modals.helpSupport.errorFailed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="help-support-page documents-page">
      <CommonPageHeader
        icon={<i className="bi bi-plus-circle" />}
        title={t("app.helpSupport.newTicket", { defaultValue: "New ticket" })}
        description={t("app.helpSupport.createSubtitle", {
          defaultValue: "Describe the issue clearly so we can help you faster.",
        })}
        onBack={() => history.push("/help-support")}
        backLabel={t("app.common.back", { defaultValue: "Back" })}
      />

      <form className="help-support-form" onSubmit={handleSubmit}>
        <div className="help-support-field">
          <label htmlFor="ticket-title">{t("app.modals.helpSupport.titleLabel")}</label>
          <input
            id="ticket-title"
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder={t("app.modals.helpSupport.titlePlaceholder")}
            disabled={submitting}
            maxLength={200}
          />
          {errors.title ? <span className="help-support-field-error">{errors.title}</span> : null}
        </div>

        <div className="help-support-form-row">
          <div className="help-support-field">
            <label htmlFor="ticket-type">{t("app.modals.helpSupport.problemType")}</label>
            <select
              id="ticket-type"
              value={form.problemType}
              onChange={(e) => setField("problemType", e.target.value)}
              disabled={submitting}
            >
              <option value="">{t("app.modals.helpSupport.problemTypePlaceholder")}</option>
              {problemTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(`app.modals.helpSupport.${opt.labelKey}`, { defaultValue: opt.defaultValue })}
                </option>
              ))}
            </select>
            {errors.problemType ? (
              <span className="help-support-field-error">{errors.problemType}</span>
            ) : null}
          </div>

          <div className="help-support-field">
            <label htmlFor="ticket-priority">
              {t("app.helpSupport.priority", { defaultValue: "Priority" })}
            </label>
            <select
              id="ticket-priority"
              value={form.priority}
              onChange={(e) => setField("priority", e.target.value)}
              disabled={submitting}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="help-support-field">
          <label htmlFor="ticket-description">{t("app.modals.helpSupport.description")}</label>
          <textarea
            id="ticket-description"
            rows={7}
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder={t("app.modals.helpSupport.descriptionPlaceholder")}
            disabled={submitting}
            maxLength={2000}
          />
          {errors.description ? (
            <span className="help-support-field-error">{errors.description}</span>
          ) : null}
        </div>

        <div className="help-support-actions">
          <button
            type="button"
            className="help-support-secondary-btn"
            onClick={() => history.push("/help-support")}
            disabled={submitting}
          >
            {t("app.common.cancel")}
          </button>
          <button type="submit" className="help-support-primary-btn" disabled={submitting}>
            {submitting
              ? t("app.common.saving", { defaultValue: "Saving…" })
              : t("app.modals.helpSupport.submit")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HelpSupportCreatePage;
